/* Custom Search — the V1 "Custom Search" window, ported.
 *
 * V1 source: lib_search.pl `dosavecustomsearch` (~3283) for the split/clean rules,
 * defs.pl `@customsearcharray` (~3536) for the Search By list and its order.
 *
 * Paste a list of values, pick ONE field, match any of them. Distinct from the
 * header's bulk paste (which probes every facet at once).
 */

export interface CustomSearchField {
  /** Wire value — must match the backend's CUSTOM_SEARCH_BY. */
  value: string;
  /** V1's uppercase label, so the dropdown reads the same as the legacy app. */
  label: string;
}

/** V1's @customsearcharray order. Deliberately omits the V1 entries not in this
 *  port (serialnumber, quotenumber, v2quotenumber, driver pay, and ac_invoicenumber
 *  — the separate Addl Cost vendor invoice) — v2 quoting is retired and the rest
 *  aren't wired up yet. */
export const CUSTOM_SEARCH_FIELDS: CustomSearchField[] = [
  { value: 'pronumber', label: 'PRO NUMBER' },
  { value: 'container', label: 'EQUIPMENT #' },
  { value: 'masterpro', label: 'MASTER PRO NUMBER' },
  { value: 'bol', label: 'BOL/BKG/REF#' },
  { value: 'po', label: 'PO' },
  { value: 'apptnumber', label: 'APPT #' },
  { value: 'invall_billtorefno', label: 'BILL TO REF # (ALL)' },
  { value: 'chassisid', label: 'CHASSIS ID' },
  { value: 'brokerrefno', label: 'BROKER/FORWARDER REF #' },
  { value: 'lotnumber', label: 'LOT NUMBER' },
  { value: 'invoicenumber', label: 'INVOICE #' },
];

export const DEFAULT_CUSTOM_SEARCH_FIELD = 'pronumber';

/** Fields V1 splits on newlines ALONE, because their values may legitimately
 *  contain spaces and commas. Every other field also splits on whitespace/commas. */
export const NEWLINE_ONLY_FIELDS = ['lotnumber'];

/** Port of V1's stripquotes (funcs.pl:3795): fold Word/Excel smart quotes back to
 *  ASCII, drop anything that isn't printable ASCII (non-breaking and zero-width
 *  spaces from spreadsheet pastes would otherwise make a value unmatchable), then
 *  trim. Runs per-value AFTER the split, exactly as V1 orders it. */
function stripquotes(value: string): string {
  return value
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\n\r\x20-\x7E]/g, '')
    .trim();
}

/** Split a pasted blob into search values using V1's per-field rules.
 *
 *  The wide separator is ASCII whitespace + comma, NOT JS `\s`: JS counts
 *  non-breaking/Unicode spaces as whitespace while Perl's `\s` does not, so `\s`
 *  would split a spreadsheet's "AB<nbsp>CD" into two bogus terms. V1 keeps it one
 *  token and lets stripquotes drop the invisible character. */
export function splitCustomSearchValues(field: string, raw: string): string[] {
  const separator = NEWLINE_ONLY_FIELDS.includes(field) ? /[\r\n]+/ : /[ \t\n\r\f\v,]+/;
  return (raw || '')
    .split(separator)
    .map(stripquotes)
    .filter(Boolean);
}

/** Split a pasted blob with the WIDE separator (ASCII whitespace + comma) for callers
 *  that have no V1 Search By field to key off — TanTable's per-column list filter.
 *
 *  Shares this module's split and `stripquotes` rules rather than re-deriving them: the
 *  invisible-character handling is the part that is easy to get wrong and expensive to
 *  debug, since an nbsp or zero-width space from a spreadsheet paste makes a value
 *  unmatchable with nothing on screen to explain it. */
export function splitPastedListValues(raw: string): string[] {
  return splitCustomSearchValues('', raw);
}

/** V1 label for a field key; falls back to the key so an unknown value from a URL
 *  still renders something sensible in the results header. */
export function customSearchLabel(field: string): string {
  return CUSTOM_SEARCH_FIELDS.find((f) => f.value === field)?.label ?? field;
}
